import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { flushSync } from "react-dom";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import api, { API_BASE_URL } from "./lib/api";
import Navbar from "@/components/Navbar";
import SparkChat from "@/components/SparkChat";
import WorkerStatusBanner from "@/components/WorkerStatusBanner";
import ProtectedRoute from "@/components/ProtectedRoute";

const HomePage = lazy(() => import("@/pages/HomePage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const SignupPage = lazy(() => import("@/pages/SignupPage"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const ItemsPage = lazy(() => import("@/pages/ItemsPage"));
const Listings = lazy(() => import("@/pages/WalmartListings"));
const WalmartInventory = lazy(() => import("@/pages/WalmartInventory"));
const WalmartBuyBox = lazy(() => import("@/pages/WalmartBuyBox"));
const WalmartOrders = lazy(() => import("@/pages/WalmartOrders"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const IncentivesPage = lazy(() => import("@/pages/IncentivesPage"));
const WalmartProfitLoss = lazy(() => import("@/pages/WalmartProfitLoss"));
const ReconPage = lazy(() => import("@/pages/ReconPage"));
const AdvertisingPage = lazy(() => import("@/pages/AdvertisingPage"));
const CampaignsPage = lazy(() => import("@/pages/CampaignsPage"));
const InsightsPage = lazy(() => import("@/pages/InsightsPage"));
const MarketingPage = lazy(() => import("@/pages/MarketingPage"));
const ComparePage = lazy(() => import("@/pages/ComparePage"));
const DataSync = lazy(() => import("@/pages/DataSync"));
const FeedsPage = lazy(() => import("@/pages/FeedsPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const WfsEstimatorPage = lazy(() => import("@/pages/WfsEstimatorPage"));
const WfsServicesPage = lazy(() => import("@/pages/WfsServicesPage"));
const AdminDashboardPage = lazy(() => import("@/pages/AdminDashboardPage"));
const AdminUsersPage = lazy(() => import("@/pages/AdminUsersPage"));
const AdminStoresPage = lazy(() => import("@/pages/AdminStoresPage"));
const AdminConnectPage = lazy(() => import("@/pages/AdminConnectPage"));
const AdminStressTestPage = lazy(() => import("@/pages/AdminStressTestPage"));
const ConnectWalmartPage = lazy(() => import("@/pages/ConnectWalmartPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const SparkConnectPage = lazy(() => import("@/pages/SparkConnectPage"));
const BusinessCardPage = lazy(() => import("@/pages/BusinessCardPage"));
const TermsOfServicePage = lazy(() => import("@/pages/TermsOfServicePage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const AdminEmailSettingsPage = lazy(() => import("@/pages/AdminEmailSettingsPage"));
const AdminJobsPage = lazy(() => import("@/pages/AdminJobsPage"));
const AdminAffiliatePage = lazy(() => import("@/pages/AdminAffiliatePage"));
const AffiliateDashboardPage = lazy(() => import("@/pages/AffiliateDashboardPage"));
const AffiliatesPage = lazy(() => import("@/pages/AffiliatesPage"));
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import logoImage from "@/assets/logo.png";
import { PollingProvider } from "@/contexts/PollingContext";
import { TimezoneProvider, useUpdateTimezone } from "@/contexts/TimezoneContext";
import { useTheme, THEMES } from "@/contexts/ThemeContext";
import { isOnWalmartPendingSettings, postAuthRedirectPath, storeWalmartPending } from "@/lib/walmartOAuthPending";
import { TutorialProvider, clearTutorialSessionDismissals } from "@/tutorials/TutorialProvider";
import { PageTutorialDialog } from "@/tutorials/PageTutorialDialog";


// Helper to decode user_info cookie (JWT)
const decodeUserInfoCookie = () => {
  try {
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
      const [key, value] = cookie.trim().split('=');
      acc[key] = value;
      return acc;
    }, {});
    
    if (cookies.user_info) {
      const payload = cookies.user_info.split('.')[1];
      const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return decoded;
    }
    return null;
  } catch (e) {
    console.error('Failed to decode user info cookie:', e);
    return null;
  }
};

const CARD_ROUTES = ["/steve", "/raph"];

function NavbarShell({ isAuthenticated, user, onLogout }) {
  const location = useLocation();
  const pathname = location.pathname.replace(/\/$/, "") || "/";
  if (CARD_ROUTES.includes(pathname)) return null;
  return (
    <>
      <Navbar isAuthenticated={isAuthenticated} user={user} onLogout={onLogout} />
      <WorkerStatusBanner isAuthenticated={isAuthenticated} />
      <SparkChat isAuthenticated={isAuthenticated} />
    </>
  );
}

function TimezoneSync({ timezone }) {
  const update = useUpdateTimezone();
  useEffect(() => {
    update(timezone || null);
  }, [timezone, update]);
  return null;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const { theme, setTheme } = useTheme();
  const themeReadyToSync = useRef(false);

  useEffect(() => {
    const validateAuth = async () => {
      const isAuthCallback = window.location.pathname === '/auth-callback';
      
      const urlParams = new URLSearchParams(window.location.search);
      const oauthError = urlParams.get('oauth_error');
      const oauthErrorDesc = urlParams.get('error_description');
      if (oauthError) {
        window.history.replaceState({}, document.title, window.location.pathname);
        setTimeout(() => {
          toast.error(`Walmart connection failed: ${oauthErrorDesc || oauthError}`, { duration: 8000 });
        }, 500);
      }
      const walmartCallbackUri = urlParams.get('walmartCallbackUri');
      const walmartPending = urlParams.get('walmart_pending');
      if (walmartPending) {
        storeWalmartPending(walmartPending);
      }
      
      if (walmartCallbackUri) {
        localStorage.setItem('walmart_callback_uri', walmartCallbackUri);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      
      let isUserAuthenticated = false;
      let userData = null;
      
      try {
        const response = await api.get(`/auth/session`);
        
        if (response.data.authenticated) {
          userData = response.data.user;
          if (userData.theme_preference && THEMES[userData.theme_preference]) {
            setTheme(userData.theme_preference);
          }
          themeReadyToSync.current = true;
          setUser(userData);
          setIsAuthenticated(true);
          isUserAuthenticated = true;
          
          if (isAuthCallback) {
            window.history.replaceState({}, document.title, '/dashboard');
            toast.success(`Welcome${userData.name ? `, ${userData.name}` : ''}!`);
          }
          
          const storedCallbackUri = localStorage.getItem('walmart_callback_uri');
          if (storedCallbackUri) {
            localStorage.removeItem('walmart_callback_uri');
            window.location.href = `/settings?walmart_callback_uri=${encodeURIComponent(storedCallbackUri)}`;
            return;
          }

          const pendingOAuthPath = postAuthRedirectPath(null);
          if (pendingOAuthPath && !isOnWalmartPendingSettings()) {
            window.location.replace(pendingOAuthPath);
            return;
          }

          setAuthLoading(false);
          return;
        }
      } catch (error) {
        console.log('No cookie session found');
      }
      
      if (walmartCallbackUri || localStorage.getItem('walmart_callback_uri')) {
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
          return;
        }
        setAuthLoading(false);
        return;
      }
      
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      setAuthLoading(false);
    };

    validateAuth();
  }, []);

  useEffect(() => {
    if (!themeReadyToSync.current || !isAuthenticated) return;
    const timer = setTimeout(() => {
      api.patch(`/auth/preferences`, { theme_preference: theme })
        .catch(() => {});
    }, 800);
    return () => clearTimeout(timer);
  }, [theme]);

  const handleLogin = (userData) => {
    const userObj = userData?.user || userData;
    if (userObj?.theme_preference && THEMES[userObj.theme_preference]) {
      setTheme(userObj.theme_preference);
    }
    themeReadyToSync.current = true;
    flushSync(() => {
      setUser(userObj);
      setIsAuthenticated(true);
    });
  };

  const refreshUser = async () => {
    try {
      const response = await api.get(`/auth/session`);
      if (response.data.authenticated && response.data.user) {
        setUser(response.data.user);
      }
    } catch (e) {
      console.warn('Failed to refresh user session:', e);
    }
  };

  const handleTutorialPrefsChange = (next) => {
    setUser((prev) => (prev ? { ...prev, tutorial_prefs: next } : prev));
  };

  const handleLogout = async () => {
    clearTutorialSessionDismissals();
    setUser(null);
    setIsAuthenticated(false);
    toast.success("Logging out...");
    try {
      const { signOutFirebase } = await import('./firebase');
      await signOutFirebase();
    } catch (e) {}
    window.location.href = `${API_BASE_URL}/auth/logout`;
  };

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-900 dark:text-white text-center">
          <img src={logoImage} alt="SparkSeller" className="w-12 h-12 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <TimezoneProvider>
    <PollingProvider>
    <TimezoneSync timezone={user?.timezone} />
    <div className="App">
      <Toaster position="top-right" richColors duration={5000} />
      
      <BrowserRouter>
        <TutorialProvider
          user={user}
          isAuthenticated={isAuthenticated}
          onTutorialPrefsChange={handleTutorialPrefsChange}
        >
        <NavbarShell isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
        <Suspense fallback={
          <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
            <img src={logoImage} alt="SparkSeller" className="w-12 h-12 animate-pulse" />
          </div>
        }>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <HomePage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/login" element={isAuthenticated ? <Navigate to={postAuthRedirectPath()} replace /> : <LoginPage onLogin={handleLogin} />} />
          <Route path="/signup" element={isAuthenticated ? <Navigate to={postAuthRedirectPath()} replace /> : <SignupPage onLogin={handleLogin} />} />
          <Route path="/auth-callback" element={<Navigate to="/dashboard" replace />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/connect/walmart" element={<ConnectWalmartPage />} />
          <Route path="/steve" element={<BusinessCardPage personKey="steve" />} />
          <Route path="/raph" element={<BusinessCardPage personKey="raph" />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/affiliates" element={<AffiliatesPage />} />
          
          {/* Protected Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Dashboard user={user} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/items" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_walmart" requiredSection="items">
                <ItemsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/walmart/listings"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_walmart" requiredSection="listings">
                <Listings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/walmart/inventory" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_walmart" requiredSection="inventory">
                <WalmartInventory />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/walmart/buybox" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_walmart" requiredSection="buybox">
                <WalmartBuyBox />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/walmart/orders" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_walmart" requiredSection="orders">
                <WalmartOrders />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/walmart/pricing" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_walmart" requiredSection="pricing">
                <PricingPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/walmart/incentives"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_walmart" requiredSection="pricing">
                <IncentivesPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/walmart/profit-loss" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_walmart" requiredSection="profit_loss">
                <WalmartProfitLoss />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/walmart/recon" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_walmart" requiredSection="recon">
                <ReconPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/walmart/advertising" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_walmart" requiredSection="advertising">
                <AdvertisingPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/walmart/campaigns" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_walmart" requiredSection="campaigns">
                <CampaignsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/walmart/insights" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_walmart" requiredSection="insights">
                <InsightsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/walmart/marketing" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_walmart" requiredSection="marketing">
                <MarketingPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/walmart/compare" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_walmart" requiredSection="compare">
                <ComparePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/walmart/data-sync" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_walmart" requiredSection="data_sync">
                <DataSync />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/walmart/feeds" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_walmart" requiredSection="feeds">
                <FeedsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <SettingsPage onUserRefresh={refreshUser} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/wfs-estimator" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_walmart" requiredSection="wfs_estimator">
                <WfsEstimatorPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/walmart/wfs-services" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="can_access_walmart" requiredSection="wfs_services">
                <WfsServicesPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="is_admin">
                <AdminDashboardPage />
              </ProtectedRoute>
            } 
          />
          <Route path="/admin/users" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/stores" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/email-settings" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/jobs" element={<Navigate to="/admin" replace />} />
          <Route 
            path="/admin/connect" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="is_admin">
                <AdminConnectPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/stress-test" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="is_admin">
                <AdminStressTestPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/affiliates" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="is_admin">
                <AdminAffiliatePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/affiliate-dashboard" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <AffiliateDashboardPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/spark-connect" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <SparkConnectPage />
              </ProtectedRoute>
            } 
          />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        <PageTutorialDialog />
        </TutorialProvider>
      </BrowserRouter>
    </div>
    </PollingProvider>
    </TimezoneProvider>
  );
}

export default App;
